import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { AdminProject } from 'features/projects';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useGetCasesQuery, useLazyGetCaseConversationQuery, useUpdateCaseMutation } from '../../api/cases';
import { useGetScenariosQuery } from '../../api/scenarios';
import { promoteScenarioValues, CASE_STATUS_LABELS, type AgentCase, type AgentCaseStatus } from '../../model/case';
import { categoriesFromScenarios } from '../../model/scenario';
import { type ScenarioFormValues } from '../../model/scenario-form';
import ScenarioFormDialog from '../scenarios/ScenarioFormDialog';
import ScenariosPagination from '../scenarios/ScenariosPagination';
import CaseConversationDialog from './CaseConversationDialog';
import CaseResolveDialog, { type CaseResolveAction } from './CaseResolveDialog';
import CasesTable from './CasesTable';

const PAGE_SIZE = 20;
const ACTION_ERROR_FALLBACK = 'No se pudo completar la acción. Intenta nuevamente.';

type StatusFilter = AgentCaseStatus | 'all';
type ResolveState = { action: CaseResolveAction; agentCase: AgentCase };
type PromoteState = { agentCase: AgentCase; initialValues: ScenarioFormValues };

interface CasesSectionProps {
  masterProject: { id: number; name: string };
  subProjects: AdminProject[];
}

const CasesSection = ({ masterProject, subProjects }: CasesSectionProps): JSX.Element => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [page, setPage] = useState(1);
  const [resolveState, setResolveState] = useState<ResolveState | null>(null);
  const [promoteState, setPromoteState] = useState<PromoteState | null>(null);
  const [previewCase, setPreviewCase] = useState<AgentCase | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, masterProject.id]);

  const { data, isLoading, isFetching, isError, refetch } = useGetCasesQuery({
    projectId: masterProject.id,
    status: statusFilter === 'all' ? undefined : statusFilter,
    page,
    limit: PAGE_SIZE,
  });

  const cases = data?.data ?? [];
  const pagination = data?.pagination;

  const { data: categoriesSource } = useGetScenariosQuery(
    { projectId: masterProject.id, page: 1, limit: 100 },
    { skip: promoteState === null }
  );
  const categories = useMemo(() => categoriesFromScenarios(categoriesSource?.data ?? []), [categoriesSource]);

  const subProjectNames = useMemo(
    () => Object.fromEntries(subProjects.map((sub) => [sub.id, sub.name])) as Record<number, string>,
    [subProjects]
  );

  const [updateCase, { isLoading: isUpdating }] = useUpdateCaseMutation();
  const [fetchCaseConversation] = useLazyGetCaseConversationQuery();

  const handlePromote = async (agentCase: AgentCase): Promise<void> => {
    setActionError(null);
    try {
      const { conversation } = await fetchCaseConversation(
        { caseId: agentCase.id, upToCaseMessage: true },
        true
      ).unwrap();
      setPromoteState({ agentCase, initialValues: promoteScenarioValues(agentCase, conversation) });
    } catch {
      setPromoteState({ agentCase, initialValues: promoteScenarioValues(agentCase) });
    }
  };

  const handleResolve = async (resolutionNote: string): Promise<void> => {
    if (!resolveState) return;
    setActionError(null);
    try {
      await updateCase({
        caseId: resolveState.agentCase.id,
        body: {
          status: resolveState.action === 'attend' ? 'attended' : 'rejected',
          ...(resolutionNote.length > 0 ? { resolutionNote } : {}),
        },
      }).unwrap();
      setResolveState(null);
    } catch (err) {
      setResolveState(null);
      setActionError(extractErrorMessage(err, ACTION_ERROR_FALLBACK));
    }
  };

  const handleScenarioCreated = async (scenarioId: number): Promise<void> => {
    if (!promoteState) return;
    setActionError(null);
    try {
      await updateCase({
        caseId: promoteState.agentCase.id,
        body: { status: 'attended', agentTestScenarioId: scenarioId },
      }).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err, 'El escenario se creó, pero no se pudo actualizar la evaluación.'));
    } finally {
      setPromoteState(null);
    }
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {(['pending', 'attended', 'rejected', 'all'] as const).map((filter) => (
          <button
            key={filter}
            type="button"
            onClick={() => setStatusFilter(filter)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
              statusFilter === filter
                ? 'border-brand-vivid bg-brand-vivid/10 text-brand-vivid'
                : 'border-border-default bg-bg-surface text-text-muted hover:text-text-default'
            )}
          >
            {filter === 'all' ? 'Todos' : CASE_STATUS_LABELS[filter]}
          </button>
        ))}
      </div>

      {actionError ? (
        <div role="alert" className="rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error">
          {actionError}
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center rounded-xl border border-border-default bg-bg-surface-alt py-14">
          <p className="m-0 text-sm text-text-muted font-body">Cargando evaluaciones…</p>
        </div>
      ) : null}

      {isError && !isLoading ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-xl border border-error/30 bg-error-light px-6 py-10 text-center"
        >
          <p className="m-0 text-sm text-error font-body">No se pudieron cargar las evaluaciones.</p>
          <Button type="button" variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError && cases.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border-default
            bg-bg-surface-alt px-6 py-14 text-center"
          role="status"
        >
          <p className="m-0 max-w-md text-sm text-text-muted font-body">
            {statusFilter === 'pending'
              ? 'No hay evaluaciones pendientes. Los admins pueden crearlas desde la vista de chat de Lidz.'
              : 'No hay evaluaciones con este filtro.'}
          </p>
        </div>
      ) : null}

      {!isLoading && !isError && cases.length > 0 ? (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <CasesTable
              cases={cases}
              masterProjectId={masterProject.id}
              subProjectNames={subProjectNames}
              onPreviewConversation={setPreviewCase}
              onPromote={(target) => void handlePromote(target)}
              onAttend={(target) => setResolveState({ action: 'attend', agentCase: target })}
              onReject={(target) => setResolveState({ action: 'reject', agentCase: target })}
            />
          </div>
          {pagination && pagination.totalPages > 1 ? (
            <div className="shrink-0">
              <ScenariosPagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                totalPages={pagination.totalPages}
                isFetching={isFetching}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </>
      ) : null}

      <CaseConversationDialog agentCase={previewCase} onClose={() => setPreviewCase(null)} />

      <CaseResolveDialog
        action={resolveState?.action ?? 'attend'}
        open={resolveState !== null}
        isLoading={isUpdating}
        onCancel={() => setResolveState(null)}
        onConfirm={(note) => void handleResolve(note)}
      />

      <ScenarioFormDialog
        mode="create"
        scenario={null}
        masterProject={masterProject}
        subProjects={subProjects}
        categories={categories}
        open={promoteState !== null}
        initialValues={promoteState?.initialValues ?? null}
        onCreated={(scenario) => void handleScenarioCreated(scenario.id)}
        onOpenChange={(open) => {
          if (!open) setPromoteState(null);
        }}
      />
    </section>
  );
};

export default CasesSection;
